import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { startTrial } from "../lib/api";
import { UserPlus, ShieldCheck, Gift, Sparkles } from "lucide-react";
import RedDisclaimer from "../components/RedDisclaimer";

export default function Register() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/dashboard";
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agreed) {
      setErr("You must agree to the Disclaimer and Terms of Service to create an account.");
      return;
    }
    setBusy(true);
    try {
      await register(email, pw, name, { agreed_to_terms: true, terms_version: "1.0" });
      // Auto-activate the 7-day trial on successful registration.
      // The endpoint is idempotent — if a trial already exists it's a no-op.
      try { await startTrial(); } catch (_) { /* non-fatal */ }
      nav(nextUrl, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center px-6 py-6">
      {/* PROMINENT TRIAL BANNER — visible above the form so visitors land
          knowing exactly what they're getting before they sign up. */}
      <div
        data-testid="register-trial-banner"
        className="w-full max-w-md mb-4 border border-amber-400/60 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent p-3"
      >
        <div className="flex items-start gap-3">
          <Gift size={18} className="text-amber-300 mt-0.5 shrink-0" />
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">
              <Sparkles size={10} /> SIGN_UP &amp; START_FREE_TRIAL
            </div>
            <div className="font-mono text-[14px] text-white mt-1 leading-snug">
              7 days, every feature, <span className="text-amber-300">+5 bonus MindStein usages</span>.
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
              Trial activates automatically the moment you finish signing up.
              No card required.
            </div>
          </div>
        </div>
      </div>

      <form
        onSubmit={submit}
        data-testid="register-form"
        className="w-full max-w-md border border-[#27272A] bg-[#0E0E0E] p-6 space-y-4"
      >
        <RedDisclaimer testid="register-red-disclaimer" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          SIGN_UP
        </div>
        <h2 className="font-mono text-2xl tracking-tight">Create your account</h2>
        <p className="text-[12px] text-zinc-500">
          You&apos;ll start on the <span className="text-amber-300">7-day free trial</span>{" "}
          (all features + 5 bonus MindStein usages). Upgrade any time to Pro, Elite, or
          Franchise.
        </p>

        <div
          data-testid="google-signup-coming-soon"
          className="w-full h-10 flex items-center justify-center gap-2 border border-dashed border-[#27272A] bg-[#0A0A0A] text-[11px] font-mono uppercase tracking-wider text-zinc-500 cursor-not-allowed select-none"
          aria-disabled="true"
          title="Google sign-in is coming soon — please use email + password for now."
        >
          GOOGLE_SIGN-IN · COMING_SOON
        </div>

        <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-zinc-600">
          <div className="flex-1 h-px bg-[#27272A]" />
          EMAIL_+_PASSWORD
          <div className="flex-1 h-px bg-[#27272A]" />
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">NAME</div>
          <input
            data-testid="register-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full h-9 px-2 mt-1 bg-[#0A0A0A] border border-[#27272A] focus:border-amber-400 outline-none text-[12px] font-mono"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">EMAIL</div>
          <input
            data-testid="register-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-9 px-2 mt-1 bg-[#0A0A0A] border border-[#27272A] focus:border-amber-400 outline-none text-[12px] font-mono"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">PASSWORD</div>
          <input
            data-testid="register-password"
            type="password"
            required
            minLength={8}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full h-9 px-2 mt-1 bg-[#0A0A0A] border border-[#27272A] focus:border-amber-400 outline-none text-[12px] font-mono"
          />
        </label>

        {/* Mandatory consent — required before account creation */}
        <label
          data-testid="register-terms-row"
          className="block border border-amber-500/30 bg-amber-500/[0.04] p-3 cursor-pointer"
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              data-testid="register-agree-checkbox"
              checked={agreed}
              onChange={(e) => {
                setAgreed(e.target.checked);
                if (e.target.checked) setErr("");
              }}
              className="mt-0.5 w-4 h-4 accent-amber-400"
            />
            <div className="text-[11.5px] text-zinc-300 leading-relaxed">
              <span className="inline-flex items-center gap-1 text-amber-300 font-mono uppercase tracking-wider text-[10px] mb-1">
                <ShieldCheck size={11} /> REQUIRED
              </span>
              <div>
                <b>[ I agree to the Terms &amp; Conditions ]</b> — I have read the{" "}
                <Link
                  to="/legal"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="register-terms-link"
                  className="text-amber-300 hover:text-white underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="register-privacy-link"
                  className="text-emerald-300 hover:text-white underline"
                >
                  Privacy Policy
                </Link>
                , including the Flexibility Subscription Terms &amp; Rebate
                Policy addendum.
                <b className="block text-red-300 mt-1">
                  I will not use MindStein for arms, weapons, military,
                  defense-aerospace, or any US ITAR / Canadian export-controlled
                  purposes.
                </b>
              </div>
            </div>
          </div>
        </label>

        {err && (
          <div className="border border-rose-500/40 bg-rose-500/10 text-rose-200 text-[11px] font-mono p-2" data-testid="register-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          data-testid="register-submit"
          disabled={busy || !agreed}
          title={!agreed ? "Agree to the Terms to continue" : ""}
          className="w-full h-10 bg-white text-black hover:bg-amber-300 font-mono uppercase text-[12px] tracking-wider disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          <UserPlus size={12} />
          {busy ? "CREATING…" : "CREATE_ACCOUNT"}
        </button>
        <div className="text-[11px] text-zinc-500 font-mono">
          Have an account?{" "}
          <Link to="/login" className="text-amber-300 hover:underline" data-testid="goto-login">
            Sign in →
          </Link>
        </div>
      </form>
    </div>
  );
}
